import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import { CloseIcon } from './icons';
import { SectionChips } from './section-chips';
import {
  FilterGroupTitle,
  FiltersBody,
  FiltersClearLink,
  FiltersCloseButton,
  FiltersOverlay,
  FiltersPanel,
  FiltersPanelCtaButton,
  FiltersPanelFooter,
  FiltersPanelHeader,
  FiltersPanelTitle,
  FiltersSheet,
  FiltersSheetClearButton,
  FiltersSheetCtaButton,
  FiltersSheetFooter,
  FiltersSheetHandle,
  FiltersSheetHeader,
  FiltersSheetTitle,
} from './styles';
import { ModalPortal } from 'components/modals/modal-portal/modal-portal';
import { useDialogBehavior } from '../hooks/use-dialog-behavior.hook';
import { useFloorplanSectionFilter } from '../hooks/use-floorplan-section-filter.hook';
import { useIsSmallViewportWidth } from 'hooks/use-is-small-viewport-width.hook';

interface FloorplanFiltersModalProps {
  onCloseModal: VoidFunction;
  onCTAClick: VoidFunction;
}

const TITLE_ID = 'floorplan-filters-title';

/**
 * The map's Filters surface, from the design: section chips over a bottom sheet on mobile and a
 * centred panel on the web, with the buttons each surface calls for — the sheet pairs "Clear all"
 * with a CTA counting what the map will show, the panel keeps "Clear all" as a text link.
 *
 * Sections are the only group for now — features (double monitor, by the window, height adjustable)
 * are a separate ticket, and the BE does not return resource attributes here yet.
 *
 * Deliberately not the shared `FilterModal`: the design's sheet holds the map's filters, not the
 * list's date/type/capacity set. It replaces that modal only while the floorplan view is on screen.
 *
 * Filters apply as they are picked — every chip writes the url the map reads — so both the CTA and
 * the close button do the same thing: leave. The CTA is kept because the design has it, and because
 * it is the one control that says how much the current filter leaves on the map.
 */
export const FloorplanFiltersModal = ({ onCloseModal, onCTAClick }: FloorplanFiltersModalProps): JSX.Element => {
  const intl = useIntl();
  const isMobileDevice = useIsSmallViewportWidth();
  const { sections, selectedSectionUuid, activeFilterCount, matchingCount, isLoading, onSectionSelect, onClearAll } =
    useFloorplanSectionFilter();
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogBehavior(dialogRef, onCloseModal);

  const dialogProps = {
    ref: dialogRef,
    'data-testid': 'floorplan-filters-modal',
    role: 'dialog',
    'aria-modal': true,
    'aria-labelledby': TITLE_ID,
    tabIndex: -1,
  };

  const closeButton = (
    <FiltersCloseButton
      type="button"
      data-testid="floorplan-filters-close"
      aria-label={intl.formatMessage({ id: 'close' })}
      onClick={onCloseModal}
    >
      <CloseIcon />
    </FiltersCloseButton>
  );

  const sectionsGroup = (
    <div>
      <FilterGroupTitle>{intl.formatMessage({ id: 'sections' })}</FilterGroupTitle>
      <SectionChips sections={sections} selectedSectionUuid={selectedSectionUuid} onSelect={onSectionSelect} />
    </div>
  );

  return (
    <ModalPortal>
      <FiltersOverlay data-testid="floorplan-filters-overlay" onClick={onCloseModal} />
      {isMobileDevice ? (
        <FiltersSheet {...dialogProps}>
          <FiltersSheetHandle />
          <FiltersSheetHeader>
            <FiltersSheetTitle id={TITLE_ID}>{intl.formatMessage({ id: 'filters' })}</FiltersSheetTitle>
            {closeButton}
          </FiltersSheetHeader>
          <FiltersBody>{sectionsGroup}</FiltersBody>
          <FiltersSheetFooter>
            <FiltersSheetClearButton
              type="button"
              data-testid="clear-all-button"
              disabled={activeFilterCount === 0}
              onClick={onClearAll}
            >
              {intl.formatMessage({ id: 'clearAll' })}
            </FiltersSheetClearButton>
            <FiltersSheetCtaButton
              type="button"
              data-testid="show-results-button"
              disabled={isLoading}
              onClick={onCTAClick}
            >
              {intl.formatMessage({ id: 'showResourcesCount' }, { count: matchingCount })}
            </FiltersSheetCtaButton>
          </FiltersSheetFooter>
        </FiltersSheet>
      ) : (
        <FiltersPanel {...dialogProps}>
          <FiltersPanelHeader>
            <FiltersPanelTitle id={TITLE_ID}>{intl.formatMessage({ id: 'filters' })}</FiltersPanelTitle>
            {closeButton}
          </FiltersPanelHeader>
          <FiltersBody hasPanelPadding>{sectionsGroup}</FiltersBody>
          <FiltersPanelFooter>
            <FiltersClearLink
              type="button"
              data-testid="clear-all-button"
              disabled={activeFilterCount === 0}
              onClick={onClearAll}
            >
              {intl.formatMessage({ id: 'clearAll' })}
            </FiltersClearLink>
            <FiltersPanelCtaButton
              type="button"
              data-testid="show-results-button"
              disabled={isLoading}
              onClick={onCTAClick}
            >
              {intl.formatMessage({ id: 'showResults' })}
            </FiltersPanelCtaButton>
          </FiltersPanelFooter>
        </FiltersPanel>
      )}
    </ModalPortal>
  );
};
